import { MapPin, Phone, Mail, Copy, Check } from "lucide-react";
import { useState } from "react";
import { WorkLinksPreview } from "@/components/build/WorkLinksPreview";

// Read-only "Context" zone (Part A, 2026-02) — matches NJM's Google-Sheet interview format's
// left-hand block exactly: Candidate Name, Role, Work Links, Job Experience, Place,
// Specialisation. Shared verbatim between the live Interview page (InterviewQueue.jsx) and the
// read-only Past Interviews detail view — both backend endpoints return the identical
// `job_experience`/`specialisation` composite shapes so the two pages never visually drift.

export function jobExperienceLabel(je) {
  if (!je) return "—";
  if (je.experience_level) return je.experience_level;
  if (je.experience_years != null) return `${je.experience_years} year${je.experience_years === 1 ? "" : "s"}`;
  return "—";
}

function SpecialisationChips({ spec }) {
  if (!spec) return <span className="text-faint text-xs">—</span>;
  const roleSpecificValues = Object.values(spec.role_specific || {}).flatMap((v) => (Array.isArray(v) ? v : [v]));
  const chips = [...(spec.secondary_roles || []).filter((r) => r !== spec.primary_role), ...(spec.skills || []), ...roleSpecificValues].filter(Boolean);
  if (!chips.length) return <span className="text-faint text-xs">{spec.primary_role || "—"}</span>;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="interview-context-specialisation-chips">
      {chips.map((c, i) => <span key={i} className="rounded-full bg-surface2 px-2.5 py-1 text-[11px] text-ink-soft">{c}</span>)}
    </div>
  );
}

// Internal-only contact chip (phone / email) — click-to-call / mailto + one-tap copy, so a
// producer can chase a candidate who no-shows their slot. STAFF-ONLY: this component renders on
// internal interview / freelancer-admin views only; phone/email is never in any agency payload.
function ContactChip({ icon: Icon, href, value, copyLabel, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = (e) => {
    e.preventDefault(); e.stopPropagation();
    navigator.clipboard?.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1400); });
  };
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-surface2 pl-2.5 pr-1.5 py-1" data-testid={testid}>
      <Icon className="h-3.5 w-3.5 text-faint" />
      <a href={href} className="text-sm font-medium text-ink hover:text-brand hover:underline" data-testid={`${testid}-link`}>{value}</a>
      <button onClick={copy} title={`Copy ${copyLabel}`} aria-label={`Copy ${copyLabel}`}
        className="ml-0.5 rounded-full p-1 text-faint hover:text-ink hover:bg-hairline transition-colors" data-testid={`${testid}-copy`}>
        {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
      </button>
    </span>
  );
}

export function ContactStrip({ phone, email }) {
  if (!phone && !email) {
    return <div className="text-xs text-faint italic" data-testid="interview-contact-none">No phone or email on file.</div>;
  }
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="interview-contact-strip">
      {phone && <ContactChip icon={Phone} href={`tel:${phone}`} value={phone} copyLabel="phone number" testid="interview-contact-phone" />}
      {email && <ContactChip icon={Mail} href={`mailto:${email}`} value={email} copyLabel="email" testid="interview-contact-email" />}
    </div>
  );
}

export function InterviewContextZone({ detail }) {
  const dedupedSecondaryRoles = (detail.secondary_roles || []).filter((r) => r !== detail.primary_role);
  return (
    <div className="space-y-5" data-testid="interview-context-zone">
      <div>
        <h2 className="font-editorial text-2xl text-ink" data-testid="interview-context-name">{detail.name}</h2>
        <div className="flex items-center gap-2 text-sm text-ink-soft mt-1 flex-wrap" data-testid="interview-context-role">
          <span>{detail.primary_role}</span>
          {dedupedSecondaryRoles.length > 0 && <span className="text-faint">+ {dedupedSecondaryRoles.join(", ")}</span>}
        </div>
        <div className="mt-3">
          <ContactStrip phone={detail.phone} email={detail.registered_gmail || detail.email} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-md border border-hairline p-4" data-testid="interview-context-fields">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-faint">Job Experience</div>
          <div className="text-sm text-ink mt-0.5" data-testid="interview-context-job-experience">{jobExperienceLabel(detail.job_experience)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-faint">Place</div>
          <div className="text-sm text-ink mt-0.5 inline-flex items-center gap-1" data-testid="interview-context-place">
            {detail.city ? <><MapPin className="h-3 w-3 text-faint" />{detail.city}{detail.country ? `, ${detail.country}` : ""}</> : "—"}
          </div>
        </div>
        <div className="sm:col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-faint mb-1.5">Specialisation</div>
          <SpecialisationChips spec={detail.specialisation} />
        </div>
      </div>

      <div>
        <div className="text-xs font-medium uppercase tracking-wider text-faint mb-2">Work Links</div>
        <WorkLinksPreview candidate={detail} />
      </div>
    </div>
  );
}
